// Copyright 2020-2022 SubQuery Pte Ltd authors & contributors
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import yaml from 'js-yaml';
import { isEmpty } from 'lodash';

import { useContractSDK } from 'containers/contractSdk';
import { useNotification } from 'containers/notificationContext';
import { useWeb3 } from 'hooks/web3Hook';
import {
  ChainType,
  DockerRegistry,
  IndexingStatus,
  PartialIpfsDeploymentManifest,
  TQueryMetadata,
} from 'pages/project-details/types';
import { coordinatorServiceUrl, createApolloClient } from 'utils/apolloClient';
import { cat, cidToBytes32, IPFS_PROJECT_CLIENT } from 'utils/ipfs';
import { GET_PROJECT, GET_QUERY_METADATA, GET_REGISTRY_VERSIONS } from 'utils/queries';

const metadataInitValue = {
  lastProcessedHeight: 0,
  lastProcessedTimestamp: 0,
  targetHeight: 0,
  chain: '',
  specName: '',
  genesisHash: '',
  indexerHealthy: undefined,
  indexerNodeVersion: '',
  queryNodeVersion: '',
  indexerStatus: 'TERMINATED',
  queryStatus: 'TERMINATED',
};

const coordinatorClient = createApolloClient(coordinatorServiceUrl);

export const useProjectDetails = (deploymentId: string) => {
  const { notification } = useNotification();
  const projectQuery = useQuery(GET_PROJECT, {
    fetchPolicy: 'network-only',
    variables: { id: deploymentId },
  });

  useEffect(() => {
    projectQuery.refetch();
  }, [projectQuery, notification?.type]);

  return projectQuery;
};

export const useIndexingStatus = (deploymentId: string): IndexingStatus | undefined => {
  const [status, setStatus] = useState<IndexingStatus>();
  const { account } = useWeb3();
  const notificationContext = useNotification();
  const sdk = useContractSDK();

  useEffect(() => {
    if (sdk && account && deploymentId) {
      sdk.queryRegistry
        .deploymentStatusByIndexer(cidToBytes32(deploymentId), account)
        .then(({ status }) => {
          setStatus(status);
        })
        .catch((error) => console.error(error));
    }
  }, [sdk, account, deploymentId, notificationContext.notification?.type]);

  return status;
};

export const getQueryMetadata = async (id: string): Promise<TQueryMetadata> => {
  try {
    const result = await coordinatorClient.query<{ queryMetadata: TQueryMetadata }>({
      query: GET_QUERY_METADATA,
      variables: { id },
      fetchPolicy: 'network-only',
    });
    return result.data.queryMetadata;
  } catch {
    return metadataInitValue;
  }
};

export const useDeploymentStatus = (deploymentId: string) => {
  const [status, setStatus] = useState<IndexingStatus | undefined>();
  const { account } = useWeb3();
  const sdk = useContractSDK();

  const getDeploymentStatus = useCallback(async () => {
    if (!sdk || !account || !deploymentId) return;
    const { status } = await sdk.queryRegistry.deploymentStatusByIndexer(
      cidToBytes32(deploymentId),
      account
    );

    setStatus(status);
  }, [sdk, account, deploymentId]);

  useEffect(() => {
    getDeploymentStatus();
  }, [getDeploymentStatus]);

  return status;
};

export const getManifest = async (cid: string) => {
  const projectYaml = await cat(cid, IPFS_PROJECT_CLIENT);
  const resultManifest = yaml.load(projectYaml) as PartialIpfsDeploymentManifest;
  return resultManifest;
};

// TODO: migrate to docker registry module
function dockerRegistryFromChain(chainType: ChainType): DockerRegistry {
  switch (chainType) {
    case 'cosmos':
      return DockerRegistry.cosmos;
    case 'avalanche':
      return DockerRegistry.avalanche;
    default:
      return DockerRegistry.substrateNode;
  }
}

const defaultRange = {
  substrate: '>=1.1.1',
  cosmos: '>=0.1.0',
  avalanche: '>=0.1.1',
};

export const useNodeVersions = (cid: string): string[] => {
  const [getNodeVersions, { data }] = useLazyQuery(GET_REGISTRY_VERSIONS);

  const fetchNodeVersions = useCallback(async () => {
    const manifest = await getManifest(cid);
    const { dataSources, runner } = manifest;
    const runtime = dataSources[0].kind;
    const chainType = runtime.split('/')[0] as ChainType;

    const registry = dockerRegistryFromChain(chainType);
    const range = runner?.node?.version ?? defaultRange[chainType];
    getNodeVersions({ variables: { range, registry } });
  }, [cid, getNodeVersions]);

  useEffect(() => {
    fetchNodeVersions();
  }, [fetchNodeVersions]);

  const versions = data?.getRegistryVersions;
  return !isEmpty(versions) ? versions : [];
};

export const useQueryVersions = (cid: string): string[] => {
  const [getQueryVersions, { data }] = useLazyQuery(GET_REGISTRY_VERSIONS);

  const fetchQueryVersions = useCallback(async () => {
    const manifest = await getManifest(cid);
    const range = manifest.runner?.query?.version ?? '>=0.15.0';
    getQueryVersions({ variables: { range, registry: DockerRegistry.query } });
  }, [cid, getQueryVersions]);

  useEffect(() => {
    fetchQueryVersions();
  }, [fetchQueryVersions]);

  const versions = data?.getRegistryVersions;
  return !isEmpty(versions) ? versions : [];
};
